import type { Chart, ChartData, ChartOptions } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import React, { MutableRefObject, useMemo } from 'react'
import { Line } from 'react-chartjs-2'
import { useChartExport } from '../../hooks/useChartExport'
import { axisMax, shareOf, shortMonth } from '../../utils'
import { Card } from '../Card'
import { ChartBox } from '../charts/ChartBox'
import { AXIS_FONT, CHANNEL_COLOURS, COLOURS, FONTS, monthTicks, percentTick } from '../charts/theme'
import { Reveal } from '../Reveal'
import { Meaning, Metric, Note } from '../CardParts'
import { Figure } from './parts'
import type { EngagementCardProps } from './parts'
import { cappedShare, Channel, changeFrom, EngagementPoint, endLabel, inTen, shareText, spreadLabels } from './utils'

const LINES: { channel: Channel; name: string }[] = [
  { channel: 'onboarded', name: 'Onboarded' },
  { channel: 'app', name: 'App' },
  { channel: 'coaching', name: 'Coaching' },
  { channel: 'workshops', name: 'Workshops' },
]

const CHART_HEIGHT = 260
// Line-end labels sit this far right of the last point and at least this far apart.
const LABEL_OFFSET = 10
const LABEL_GAP = 18
const PLUGINS = [ChartDataLabels]

export const EngagementChartCard = ({ data, points, since, footer, highlighted, onAbout }: EngagementCardProps) => {
  const { chartRef, exportImage } = useChartExport<'line'>()
  const { enrolled, onboarded, early_days } = data.people

  // In the early days the lines are too short to say anything.
  if (early_days) return null

  const share = shareOf(onboarded, enrolled) ?? 0
  const title = share >= 50 ? `${inTen(share)} enrolled people have onboarded` : `Engagement ${since}`

  return (
    <Reveal>
      <Card
        question="Is your team using the programme?"
        title={title}
        onAbout={onAbout}
        highlighted={highlighted}
        exportImage={exportImage}
        exportName="engagement"
        footer={{ text: footer, chip: 'Everyone enrolled included' }}
      >
        <Metric text={`Share of the ${enrolled} enrolled people who have done each at least once`} />
        <Figure value={`${share}%`} label={`of ${enrolled} enrolled people have onboarded`} change={changeFrom(points, 'onboarded', enrolled)} />
        <EngagementLines points={points} enrolled={enrolled} chartRef={chartRef as MutableRefObject<Chart<'line'>>} />
        <Note text={appLeads(points)} />
        <Meaning text="Each person is counted once, the first time they onboard, set up the app, have a coaching session or attend a workshop." />
      </Card>
    </Reveal>
  )
}

// The design's one comparison, said only when the app is ahead of both.
const appLeads = (points: EngagementPoint[]): string | null => {
  const latest = points[points.length - 1]
  if (!latest || latest.app <= latest.coaching || latest.app <= latest.workshops) return null
  return 'More people have set up the app than have been coached or attended a workshop.'
}

type LinesProps = { points: EngagementPoint[]; enrolled: number; chartRef: MutableRefObject<Chart<'line'>> }

const EngagementLines = ({ points, enrolled, chartRef }: LinesProps) => {
  const data = useMemo(() => chartData(points, enrolled), [points, enrolled])
  const [animated, still] = useMemo(() => {
    const options = chartOptions(points, enrolled)
    return [options, { ...options, animation: false as const }]
  }, [points, enrolled])

  return (
    <ChartBox height={CHART_HEIGHT} label="Share of enrolled people who have onboarded, set up the app, been coached and attended a workshop, by month">
      {(animate) => <Line ref={chartRef} data={data} options={animate ? animated : still} plugins={PLUGINS} />}
    </ChartBox>
  )
}

const sharesOf = (points: EngagementPoint[], channel: Channel, enrolled: number): number[] =>
  points.map((point) => cappedShare(point[channel], enrolled))

const chartData = (points: EngagementPoint[], enrolled: number): ChartData<'line', number[], string> => {
  const last = points.length - 1

  return {
    labels: points.map((point) => shortMonth(point.month)),
    datasets: LINES.map(({ channel, name }) => ({
      label: name,
      data: sharesOf(points, channel, enrolled),
      borderColor: CHANNEL_COLOURS[channel],
      backgroundColor: CHANNEL_COLOURS[channel],
      borderWidth: 3,
      pointRadius: points.map((_, index) => (index === last ? 5 : 0)),
      pointHoverRadius: 5,
    })),
  }
}

type LabelContext = { chart: Chart; datasetIndex: number }

// How far a line's end label moves down (or up) to clear the others.
const labelShift = ({ chart, datasetIndex }: LabelContext): number => {
  const ends = chart.data.datasets.map((dataset) => chart.scales.y.getPixelForValue(Number(dataset.data[dataset.data.length - 1])))
  return spreadLabels(ends, LABEL_GAP, chart.chartArea.bottom)[datasetIndex] - ends[datasetIndex]
}

const chartOptions = (points: EngagementPoint[], enrolled: number): ChartOptions<'line'> => {
  const highest = Math.max(0, ...LINES.flatMap(({ channel }) => sharesOf(points, channel, enrolled)))
  const latest = points[points.length - 1]
  // From the counts, not the capped shares: coaching can pass enrolled.
  const endLabels = LINES.map(({ channel, name }) => (latest ? endLabel(name, latest[channel], enrolled) : name))
  const pointText = (dataIndex: number, datasetIndex: number) => shareText(points[dataIndex][LINES[datasetIndex].channel], enrolled)

  return {
    maintainAspectRatio: false,
    // Room on the right for each line's name.
    layout: { padding: { top: 12, right: 150 } },
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: { label: (item) => ` ${item.dataset.label} ${pointText(item.dataIndex, item.datasetIndex)}` },
      },
      // Each line named at its end: "Onboarded 70%".
      datalabels: {
        // Right of the point, tilted just enough to clear the other labels.
        align: (context) => (Math.atan2(labelShift(context), LABEL_OFFSET) * 180) / Math.PI,
        anchor: 'center',
        clamp: false,
        clip: false,
        color: COLOURS.ink,
        display: (context) => context.dataIndex === context.dataset.data.length - 1,
        font: { family: FONTS.heading, size: 14, weight: 600 },
        formatter: (_value: number, context) => endLabels[context.datasetIndex],
        offset: (context) => Math.hypot(LABEL_OFFSET, labelShift(context)),
      },
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        ticks: monthTicks(points.length),
      },
      y: {
        min: 0,
        max: Math.min(100, axisMax(highest)),
        grid: { color: COLOURS.grid, drawBorder: false },
        ticks: { color: COLOURS.grey600, font: AXIS_FONT, callback: percentTick },
      },
    },
  }
}
